import { getClient } from '@/utils/client';
import { SectionMapResponseType } from '@/utils/queryOptions';
import { CampaignStatus } from '@frontend/api-client-user/types';
import { AxiosError } from '@frontend/utils/api/types';
import { createServerFn } from '@tanstack/react-start';
import { format } from 'date-fns';

export const roomGroupByIdFn = createServerFn({ method: 'GET' })
	.inputValidator((data: { roomGroupId: string }) => data)
	.handler(async ({ data: { roomGroupId } }) => {
		const client = await getClient();
		return await client.request('/v1/room-groups/{id}', 'get', {
			params: { path: { id: roomGroupId } },
		});
	});

export const roomGroupsFn = createServerFn({ method: 'GET' }).handler(
	async () => {
		const client = await getClient();
		return await client.request('/v1/room-groups', 'get', {});
	},
);

export const roomGroupsThresholdsFn = createServerFn({ method: 'GET' }).handler(
	async () => {
		const client = await getClient();
		return await client.request('/v1/room-groups/thresholds', 'get', {});
	},
);

export const roomGroupUsageFN = createServerFn({ method: 'GET' })
	.inputValidator((data: { start: Date | string; end: Date | string }) => data)
	.handler(async ({ data: { start, end } }) => {
		const client = await getClient();
		return await client.request('/v1/room-group-usage', 'get', {
			params: {
				query: {
					Start: format(start, 'yyyy-MM-dd'),
					End: format(end, 'yyyy-MM-dd'),
				},
			},
		});
	});

export const sectionThresholdsFN = createServerFn({ method: 'GET' })
	.inputValidator((data: { sectionId: string[] }) => data)
	.handler(async ({ data: { sectionId } }) => {
		const client = await getClient();
		return await client.request('/v1/sections/thresholds', 'get', {
			// @ts-ignore
			params: { query: { 'section-id': sectionId } },
		});
	});

export const sectionMapFN = createServerFn({ method: 'GET' })
	.inputValidator((data: { sectionId?: string }) => data)
	.handler(async ({ data: { sectionId } }) => {
		if (!sectionId)
			return {
				id: 'unknown',
				name: 'unknown',
				premises: [],
			} as SectionMapResponseType;

		const client = await getClient();
		return await client.request('/v1/sections/{sectionId}/map', 'get', {
			params: {
				path: {
					sectionId,
				},
			},
		});
	});

export const usageFN = createServerFn({ method: 'GET' })
	.inputValidator(
		(data: { start: Date | string; end: Date | string; sectionId: string }) =>
			data,
	)
	.handler(async ({ data: { sectionId, end, start } }) => {
		const client = await getClient();
		try {
			return await client.request('/v1/usage', 'get', {
				params: {
					query: {
						'section-id': [sectionId],
						start: format(start, 'yyyy-MM-dd'),
						end: format(end, 'yyyy-MM-dd'),
					},
				},
			});
		} catch (e) {
			if (e instanceof AxiosError && e.status === 404) {
				return {
					start: format(start, 'yyyy-MM-dd'),
					end: format(end, 'yyyt-MM-dd'),
					totalUsage: 0,
					transactions: [],
					usageByTime: {
						min: { timestamp: '', value: 0 },
						max: { timestamp: '', value: 0 },
						average: 0,
					},
				};
			}

			throw e;
		}
	});

export const campaignListFN = createServerFn({ method: 'GET' })
	.inputValidator((data: { status?: CampaignStatus }) => data)
	.handler(async ({ data: { status } }) => {
		const client = await getClient();

		return await client.request('/v1/campaigns', 'get', {
			params: { query: { Status: status } },
		});
	});

export const campaignDetailFN = createServerFn({ method: 'GET' })
	.inputValidator((data: { campaignId: string }) => data)
	.handler(async ({ data: { campaignId } }) => {
		const client = await getClient();

		return await client.request('/v1/campaigns/{id}', 'get', {
			params: { path: { id: campaignId } },
		});
	});

import { db } from '@/db';
import { schemaShiftEmployees } from '@/db/schema';
import { seedRoomGroups, seedShiftEmployees } from '@/db/seed';
import { getRequest } from '@tanstack/react-start/server';

const checkSeedData = async () => {
	const result = await db.select().from(schemaShiftEmployees);

	if (result.length <= 0) {
		seedRoomGroups();
		seedShiftEmployees();
	}
};

export const sectionsFn = createServerFn({ method: 'GET' }).handler(
	async () => {
		checkSeedData();
		const client = await getClient();
		return await client.request('/v1/sections', 'get', {});
	},
);

export const roomTypesFn = createServerFn({ method: 'GET' }).handler(
	async () => {
		const client = await getClient();
		const result = await client.request('/v1/room-types', 'get', {});

		return result;
	},
);

export const getCurrentlRequestUrl = createServerFn({ method: 'GET' }).handler(
	() => {
		const req = getRequest();
		return req.url;
	},
);
